import {
  doc,
  onSnapshot,
  Timestamp,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type {
  AiVerificationDefaultCandidateCriteria,
  AiVerificationDistribution,
  AiVerificationQuestion,
  AiVerificationRandomConstraints,
  AiVerificationTemplate,
  DistrictMode,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * template ID から AiVerificationTemplate を 1 件 subscribe する hook (Sprint V)。
 *
 * admin 側 `useVerificationTemplates` の `mapVerificationTemplate` と同じ正規化を行う
 * (user 側は読み取り専用なので別 hook で実装、apps 間直接 import 禁止のため)。
 *
 * 状態:
 *   - loading: subscribe 開始前 / 開始直後
 *   - ready  : doc 取得済
 *   - not-found: templateId は指定済だが doc が存在しない
 *   - error  : Firestore error
 */

export type VerificationTemplateState =
  | { status: 'loading'; template: null; error: null }
  | { status: 'ready'; template: AiVerificationTemplate; error: null }
  | { status: 'not-found'; template: null; error: null }
  | { status: 'error'; template: null; error: Error };

function tsToIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate().toISOString();
    } catch {
      return new Date().toISOString();
    }
  }
  return new Date().toISOString();
}

function asStatus(value: unknown): AiVerificationTemplate['status'] {
  return value === 'published' || value === 'archived' ? value : 'draft';
}

/**
 * targetMode の narrow (MD-4 モードドリブン化 2026-07-24)。
 * 旧実装は組込 4 種を whitelist し custom mode (例 keikan) の targetMode を黙って null
 * (=全 mode 共通扱い) に落としていたバグ → mode キーは ModeProfile 駆動の任意 string の
 * ため、非空 string をそのまま受理する。
 */
function asTargetMode(value: unknown): DistrictMode | null {
  if (typeof value === 'string' && value.trim()) return value.trim() as DistrictMode;
  return null;
}

function asQuestions(value: unknown): AiVerificationQuestion[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (q): q is AiVerificationQuestion =>
      typeof q === 'object' && q !== null && typeof (q as { id?: unknown }).id === 'string',
  );
}

function asDistribution(value: unknown): AiVerificationDistribution | undefined {
  if (
    value === 'all_same' ||
    value === 'random_per_user' ||
    value === 'manual' ||
    value === 'free'
  ) {
    return value;
  }
  return undefined;
}

function asRandomConstraints(value: unknown): AiVerificationRandomConstraints | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as Record<string, unknown>;
  if (typeof v.minPostsPerUser !== 'number') return undefined;
  return {
    minPostsPerUser: v.minPostsPerUser,
    minOwnPosts: typeof v.minOwnPosts === 'number' ? v.minOwnPosts : 0,
    allowDuplicates: typeof v.allowDuplicates === 'boolean' ? v.allowDuplicates : false,
    preferOwnPosts: typeof v.preferOwnPosts === 'boolean' ? v.preferOwnPosts : true,
    preferLessVerified: typeof v.preferLessVerified === 'boolean' ? v.preferLessVerified : false,
  };
}

function asCandidateCriteria(value: unknown): AiVerificationDefaultCandidateCriteria | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as Record<string, unknown>;
  if (v.type !== 'all_ai_done' && v.type !== 'session_only' && v.type !== 'manual_pick') {
    return undefined;
  }
  const out: AiVerificationDefaultCandidateCriteria = { type: v.type };
  if (v.sessionScope === 'set_session' || v.sessionScope === 'all_sessions') {
    out.sessionScope = v.sessionScope;
  }
  return out;
}

/** Firestore doc → AiVerificationTemplate (admin 側の mapVerificationTemplate と同 logic)。 */
export function mapVerificationTemplate(snap: {
  id: string;
  data: () => DocumentData;
}): AiVerificationTemplate {
  const data = snap.data();
  const out: AiVerificationTemplate = {
    id: snap.id,
    displayName: typeof data.displayName === 'string' ? data.displayName : '',
    description: typeof data.description === 'string' ? data.description : '',
    targetMode: asTargetMode(data.targetMode),
    perPostQuestions: asQuestions(data.perPostQuestions),
    perClassificationQuestions: asQuestions(data.perClassificationQuestions),
    allowFreeComment: typeof data.allowFreeComment === 'boolean' ? data.allowFreeComment : true,
    status: asStatus(data.status),
    version: typeof data.version === 'number' ? data.version : 0,
    createdAt: tsToIso(data.createdAt),
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
    updatedAt: tsToIso(data.updatedAt),
    updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
  };
  const dist = asDistribution(data.defaultDistribution);
  if (dist) out.defaultDistribution = dist;
  const rc = asRandomConstraints(data.defaultRandomConstraints);
  if (rc) out.defaultRandomConstraints = rc;
  const cc = asCandidateCriteria(data.defaultCandidateCriteria);
  if (cc) out.defaultCandidateCriteria = cc;
  if (typeof data.defaultEndAtOffsetDays === 'number') {
    out.defaultEndAtOffsetDays = data.defaultEndAtOffsetDays;
  }
  return out;
}

export function useVerificationTemplate(
  districtId: string,
  templateId: string | null,
): VerificationTemplateState {
  const [state, setState] = useState<VerificationTemplateState>({
    status: 'loading',
    template: null,
    error: null,
  });

  useEffect(() => {
    if (!templateId) {
      setState({ status: 'loading', template: null, error: null });
      return;
    }
    const ref = doc(db, 'districts', districtId, 'aiVerificationTemplates', templateId);
    const unsub: Unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setState({ status: 'not-found', template: null, error: null });
          return;
        }
        setState({ status: 'ready', template: mapVerificationTemplate(snap), error: null });
      },
      (error) => setState({ status: 'error', template: null, error }),
    );
    return () => unsub();
  }, [districtId, templateId]);

  return state;
}
